// SaveV2: single persisted document under one key. Replaces v1's four keys
// (karateJump_progress, karateJump_highScore, karateJump_hints,
// karate-jump-lang) — save/migrations.ts folds those in on first load.
// wallet/unlocks/daily/leaderboard are Phase 6 fields, present from the
// start so the schema never needs a v3 for them.

export const SAVE_KEY = 'karateJump_v2';

export interface HintEntry {
  shown: number;
  done: boolean;
}

export interface LeaderboardEntry {
  initials: string;
  score: number;
  distance: number;
  district: string;
  date: string;
}

export interface SaveV2 {
  version: 2;
  highScore: number;
  bests: {
    distance: number;
    beltIndex: number;
    byDistrict: Record<string, number>;
  };
  wallet: number;
  unlocks: string[];
  equipped: Record<string, string>;
  daily: {
    bestByDate: Record<string, number>;
  };
  leaderboard: LeaderboardEntry[];
  settings: {
    musicMuted: boolean;
    hintsSeen: Record<string, HintEntry>;
  };
}

export function defaultSave(): SaveV2 {
  return {
    version: 2,
    highScore: 0,
    bests: { distance: 0, beltIndex: 0, byDistrict: {} },
    wallet: 0,
    unlocks: [],
    equipped: {},
    daily: { bestByDate: {} },
    leaderboard: [],
    settings: { musicMuted: false, hintsSeen: {} },
  };
}

/** Shallow-validating merge: unknown/corrupt fields fall back to defaults. */
export function normalizeSave(raw: unknown): SaveV2 {
  const base = defaultSave();
  if (typeof raw !== 'object' || raw === null) return base;
  const data = raw as Partial<SaveV2>;
  return {
    version: 2,
    highScore: numberOr(data.highScore, 0),
    bests: {
      distance: numberOr(data.bests?.distance, 0),
      beltIndex: numberOr(data.bests?.beltIndex, 0),
      byDistrict: recordOfNumbers(data.bests?.byDistrict),
    },
    wallet: numberOr(data.wallet, 0),
    unlocks: Array.isArray(data.unlocks) ? data.unlocks.filter((u) => typeof u === 'string') : [],
    equipped: typeof data.equipped === 'object' && data.equipped !== null ? data.equipped : {},
    daily: { bestByDate: recordOfNumbers(data.daily?.bestByDate) },
    leaderboard: Array.isArray(data.leaderboard) ? data.leaderboard : [],
    settings: {
      musicMuted: data.settings?.musicMuted === true,
      hintsSeen:
        typeof data.settings?.hintsSeen === 'object' && data.settings.hintsSeen !== null
          ? data.settings.hintsSeen
          : {},
    },
  };
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback;
}

function recordOfNumbers(value: unknown): Record<string, number> {
  if (typeof value !== 'object' || value === null) return {};
  const out: Record<string, number> = {};
  for (const [key, v] of Object.entries(value)) {
    if (typeof v === 'number' && Number.isFinite(v)) out[key] = v;
  }
  return out;
}
